import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
    Search, RefreshCw, Briefcase, FileText,
    CheckCircle2, XCircle, Clock, Calendar, PlayCircle,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Globe, Layers, ShieldCheck,
    User,
    ArrowUp, ArrowDown, ArrowUpDown,
    Download, ArrowLeft,
    Columns3, Eye, EyeOff, Check
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown "; // Adjust import path
import { useClientLevel } from '../../../hooks/useClientLevel';
import JobDetailsSidebar from "./JobDetails";
import AgentExportModal from "../../Agent/AgentExportModal";
import ScrollContainer from 'react-indiana-drag-scroll';

interface Job {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    job_id: string;
    job_name: string;
    job_type: string;
    scan_type: string | null;
    status: string;
    scheduler_id: string;
    description: string;
    agent_id: string | null;
    client_id: number;
    organization_id: number;
    progress: number;
    master_agent_id: string;
    asset_ids: string;
    target_id: string | null;
    parameters: any;
    results: any;
    error: string | null;
    hostname: string;
    client_name: string;
}

export type JobColumnKey =
    | 'job_name'
    | 'job_type'
    | 'hostname'
    | 'description'
    | 'created'
    | 'client_name'
    | 'progress'
    | 'status';

export default function Jobs({ scriptjobs }: { scriptjobs?: boolean }) {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [showExportModal, setShowExportModal] = useState(false);

    // Sidebar state
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'created', direction: 'desc' });
    const level = useClientLevel();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const urlJobType = searchParams.get("job_type");
    const isExternalScanRoute = location.pathname.includes("/external-scan");

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<JobColumnKey, boolean>>({
        job_name: true,
        job_type: true,
        hostname: true,
        description: true,
        created: true,
        client_name: true,
        progress: true,
        status: true,
    });

    const columnOptions: { key: JobColumnKey; label: string }[] = useMemo(() => {
        const options: { key: JobColumnKey; label: string }[] = [
            { key: 'job_name', label: 'Job Name' },
            { key: 'job_type', label: 'Type' },
            { key: 'hostname', label: 'Hostname' },
            { key: 'description', label: 'Description' },
            { key: 'created', label: 'Time' },
        ];
        if (level.name === 'global') {
            options.push({ key: 'client_name', label: 'Client' });
        }
        options.push(
            { key: 'progress', label: 'Progress' },
            { key: 'status', label: 'Status' }
        );
        return options;
    }, [level.name]);

    const handleColumnToggle = (column: JobColumnKey) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [column]: !prev[column],
        }));
    };

    const toggleAllColumns = () => {
        const availableKeys = columnOptions.map((o) => o.key);
        const allVisible = availableKeys.every((k) => visibleColumns[k]);
        setVisibleColumns((prev) => {
            const next = { ...prev };
            availableKeys.forEach((k) => {
                next[k] = !allVisible;
            });
            return next;
        });
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (showColumnFilter && !target.closest('[data-col-menu]')) {
                setShowColumnFilter(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColumnFilter]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = {};

            if (level.id) {
                queryObj.client_id = level.id;
            }

            if (searchTerm) {
                queryObj.job_name = { $ilike: `%${searchTerm}%` };
            }

            if (scriptjobs) {
                queryObj.job_type = "execute_script";
            } else if (urlJobType) {
                queryObj.job_type = urlJobType;
            }

            const payload: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj),
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction
            };

            const response = await baseapi.get("/jobs_view", { params: payload });

            if (response.data.status) {
                setJobs(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            } else {
                showError(response.data.message || "Failed to load jobs");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [currentPage, limit, searchTerm, level, sortConfig, scriptjobs, urlJobType]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            const newDirection = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
            return { key, direction: newDirection };
        });
        setCurrentPage(1);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchJobs();
    };

    const handleOpenDetails = (job: Job) => {
        setSelectedJob(job);
        setIsSidebarOpen(true);
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortConfig.key !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-500 transition-opacity" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />;
    };

    const renderStatusBadge = (status: string) => {
        if (!status) return <span className="text-gray-500">-</span>;

        const normalized = status.toLowerCase();

        if (normalized === "completed" || normalized === "success") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-green-500/10 text-green-400 border border-green-500/20">
                    <CheckCircle2 className="w-2.5 h-2.5" /> {status}
                </div>
            );
        } else if (normalized === "failed" || normalized === "error") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-500/10 text-red-400 border border-red-500/20">
                    <XCircle className="w-2.5 h-2.5" /> {status}
                </div>
            );
        } else if (normalized === "running" || normalized === "in_progress") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    <PlayCircle className="w-2.5 h-2.5 animate-pulse" /> {status}
                </div>
            );
        }

        return (
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-800 text-gray-300 border border-gray-700 whitespace-nowrap">
                <Clock className="w-2.5 h-2.5" /> {status}
            </div>
        );
    };

    const renderProgress = (progress: number | null | undefined) => {
        if (progress === null || progress === undefined) return <span className="text-gray-500">-</span>;

        const value = Math.max(0, Math.min(100, progress));

        let colorClass = "bg-indigo-500";
        if (value === 100) colorClass = "bg-emerald-500";
        if (value > 0 && value < 100) colorClass = "bg-blue-500";
        if (value === 0) colorClass = "bg-gray-600";

        return (
            <div className="w-full max-w-[120px] flex items-center gap-2">
                <div className="w-full bg-gray-800 rounded-full h-1.5 border border-gray-700/50 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ease-in-out ${colorClass}`}
                        style={{ width: `${value}%` }}
                    />
                </div>
                <span className={`text-[9px] font-black w-6 text-right tracking-tight ${value === 100 ? 'text-emerald-400' : 'text-gray-400'}`}>
                    {value}%
                </span>
            </div>
        );
    };

    const getDuration = (start: string, end: string) => {
        if (!start || !end) return "-";
        const diffMs = new Date(end).getTime() - new Date(start).getTime();
        const diffSec = Math.floor(diffMs / 1000);
        if (diffSec < 60) return `${diffSec}s`;
        return `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
    };

    const visibleColsCount = columnOptions.filter((o) => visibleColumns[o.key]).length || 1;

    return (
        <div className="flex flex-col bg-transparent text-gray-200 mb-20 p-2">
            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex gap-2">
                    <div className="bg-orange-600 p-1.5 rounded">
                        <Briefcase className="w-4 h-4 text-white" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Jobs</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchJobs}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <form onSubmit={handleSearch} className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search jobs..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all"
                        />
                    </form>

                    {/* Column Filter Dropdown */}
                    <div className="relative" data-col-menu>
                        <button
                            onClick={() => setShowColumnFilter(!showColumnFilter)}
                            className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2"
                        >
                            <Columns3 className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                        </button>
                        {showColumnFilter && (
                            <div className="absolute top-full mt-2 right-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                <div className="px-3 py-1.5 text-[9px] font-bold text-gray-100 uppercase border-b border-gray-800 mb-1 flex items-center justify-between">
                                    <span>Toggle Columns</span>
                                    <button
                                        onClick={toggleAllColumns}
                                        className="text-[9px] text-orange-400 hover:text-orange-300 transition-colors"
                                    >
                                        {columnOptions.every((o) => visibleColumns[o.key])
                                            ? 'Hide All'
                                            : 'Show All'}
                                    </button>
                                </div>
                                {columnOptions.map((option) => (
                                    <button
                                        key={option.key}
                                        onClick={() => handleColumnToggle(option.key)}
                                        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            {visibleColumns[option.key] ? (
                                                <Eye className="w-3 h-3 text-orange-500" />
                                            ) : (
                                                <EyeOff className="w-3 h-3 text-gray-600" />
                                            )}
                                            {option.label}
                                        </div>
                                        <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-700'}`}>
                                            {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => setShowExportModal(true)}
                        className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-gray-700 transition-all"
                    >
                        <Download className="w-4 h-4 text-cyan-500" />
                        Export
                    </button>
                    {isExternalScanRoute && (
                        <button
                            onClick={() => navigate("/external-scan")}
                            className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-gray-700 transition-all"
                        >
                            <ArrowLeft className="w-4 h-4 text-orange-500" />
                            Back
                        </button>
                    )}
                </div>
            </div>

            {/* Table Area */}
            <div className="flex flex-col">
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl flex flex-col">
                    <ScrollContainer
                        className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
                        horizontal={true}
                        vertical={false}
                        hideScrollbars={false}
                    >
                        <table className="w-full min-w-[1100px] text-left border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/50">
                                    {visibleColumns.job_name && (
                                        <th
                                            className="w-[18%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('job_name')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Briefcase size={14} className="text-cyan-400 shrink-0" />
                                                <span>Job Name</span>
                                                <SortIcon field="job_name" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.job_type && (
                                        <th
                                            className="w-[10%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('job_type')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Layers size={14} className="text-indigo-400 shrink-0" />
                                                <span>Type</span>
                                                <SortIcon field="job_type" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.hostname && (
                                        <th
                                            className="w-[12%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('hostname')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Globe size={14} className="text-emerald-400 shrink-0" />
                                                <span>Hostname</span>
                                                <SortIcon field="hostname" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.description && (
                                        <th className="w-[16%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <FileText size={14} className="text-gray-400/80 shrink-0" /> <span>Description</span>
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.created && (
                                        <th
                                            className="w-[16%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('created')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Clock size={14} className="text-purple-400 shrink-0" />
                                                <span>Time</span>
                                                <SortIcon field="created" />
                                            </div>
                                        </th>
                                    )}
                                    {level.name === "global" && visibleColumns.client_name && (
                                        <th
                                            className="w-[12%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('client_name')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <User size={14} className="text-purple-400 shrink-0" />
                                                <span>Client</span>
                                                <SortIcon field="client_name" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.progress && (
                                        <th
                                            className="w-[10%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('progress')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <PlayCircle size={14} className="text-blue-400 shrink-0" />
                                                <span>Progress</span>
                                                <SortIcon field="progress" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.status && (
                                        <th
                                            className="w-[10%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <ShieldCheck size={14} className="text-green-400 shrink-0" />
                                                <span>Status</span>
                                                <SortIcon field="status" />
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                                {loading && jobs.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-2">
                                                <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                                <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em]">Fetching Jobs...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : jobs.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                                <Briefcase size={24} className="text-gray-400" />
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">No jobs found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    jobs.map((job) => (
                                        <tr key={job.id} className="hover:bg-gray-800/40 transition-colors group" onClick={() => handleOpenDetails(job)}>
                                            {/* Whitespace nowrap to keep on 1 line */}
                                            {visibleColumns.job_name && (
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <Briefcase size={12} className="text-cyan-400 shrink-0" />
                                                        {job.job_name ? (
                                                            <button
                                                                className="text-[11px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors tracking-wide decoration-indigo-400/30 hover:decoration-indigo-300 underline-offset-4 cursor-pointer text-left"
                                                            >
                                                                {job.job_name.replace(/_/g, ' ')}
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-500">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.job_type && (
                                                <td className="px-4 py-2 whitespace-nowrap" >
                                                    {job.job_type ? (
                                                        <span className="px-1.5 py-0.5 text-white rounded text-[9px] font-bold uppercase tracking-wider inline-block max-w-[150px] truncate" title={job.job_type}>
                                                            {job.job_type.replace(/_/g, ' ')}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.hostname && (
                                                <td className="px-4 py-2 whitespace-nowrap" >
                                                    <div className="flex items-center gap-2">
                                                        <Globe size={12} className="text-emerald-400 shrink-0" />
                                                        {job.hostname ? (
                                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white uppercase tracking-wider max-w-[250px] truncate" title={job.hostname}>
                                                                {job.hostname}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-500">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {/* ONLY Description gets the truncate class */}
                                            {visibleColumns.description && (
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    {job.description ? (
                                                        <span
                                                            className="text-[10px] text-gray-400 leading-tight"
                                                            title={job.description}
                                                        >
                                                            {job.description.length > 35
                                                                ? `${job.description.substring(0, 35)}...`
                                                                : job.description}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.created && (
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-[9px] text-gray-300 font-mono flex items-start gap-1">
                                                            <Calendar size={12} className="text-gray-500 shrink-0 mt-0.5" />
                                                            <span>{job.created ? new Date(job.created + "Z").toLocaleString() : "-"}</span>
                                                        </span>
                                                        <span className="text-[8px] text-gray-500 font-mono pl-4">
                                                            Duration: {job.created && job.updated ? getDuration(job.created, job.updated) : "-"}
                                                        </span>
                                                    </div>
                                                </td>
                                            )}
                                            {level.name === "global" && visibleColumns.client_name && (
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <User size={12} className="text-purple-400 shrink-0" />
                                                        <span className="text-[9px] font-bold text-white">{job.client_name}</span>
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.progress && (
                                                <td className="px-4 py-2 whitespace-nowrap text-center align-middle">
                                                    {job.status === 'skipped' ? (
                                                        <span>-</span>
                                                    ) : (
                                                        <div className="flex justify-center">
                                                            {renderProgress(job.progress)}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.status && (
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    {renderStatusBadge(job.status)}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </ScrollContainer>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                                <span className="text-gray-300">
                                    {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronsLeft size={14} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronLeft size={14} />
                            </button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronRight size={14} />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronsRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="jobs"
                clientId={level?.id}
            />

            {/* Render Sidebar Component */}
            <JobDetailsSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                jobId={selectedJob?.job_id || null}
                clientId={selectedJob?.client_id || null}
                jobName={selectedJob?.job_name || ''}
                jobType={selectedJob?.job_type || ''}
            />
        </div>
    );
}
